import { Check } from "@mui/icons-material";
import { Box, SxProps, Theme, Typography, useTheme } from "@mui/material";
import React from "react";

interface LabelValueProps {
  label: React.ReactNode;
  value?: number | string;
  children?: React.ReactNode;
  labelWidth?: number | string;
  spacing?: number;
  valueColor?: string;
  labelColor?: string;
  showSuccessIndicator?: boolean;
  successMessage?: string;
  containerSx?: SxProps<Theme>;
  boxSx?: SxProps<Theme>;
}

export const LabelValue: React.FC<LabelValueProps> = ({
  label,
  value,
  children,
  labelWidth = "136px",
  spacing = 2,
  valueColor,
  labelColor,
  showSuccessIndicator = false,
  successMessage,
  containerSx,
  boxSx,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "flex-start", md: "center" },
        gap: { xs: "8px", md: spacing },
        height: { xs: "auto", md: 40 },
        width: "100%",
        minWidth: 0,
        ...containerSx,
      }}
    >
      <Typography
        component="div"
        sx={{
          minWidth: { xs: "auto", md: labelWidth },
          color: labelColor ?? theme.colors.text[1],
          fontWeight: 700,
          fontSize: 12,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          position: "relative",
          width: "fit-content",
          maxWidth: "100%",
          minWidth: 0,
          flex: 1,
          ...boxSx,
        }}
      >
        <Box sx={{ visibility: showSuccessIndicator ? "hidden" : "visible" }}>
          {children ?? (
            <Typography
              component="span"
              sx={{
                flex: 1,
                color: valueColor ?? theme.colors.text[5],
                fontWeight: 500,
                fontSize: 14,
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {value}
            </Typography>
          )}
        </Box>
        {showSuccessIndicator && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              padding: "12px",
              borderRadius: "8px",
              background: theme.colors.secondary[8],
              display: "flex",
              alignItems: "center",
              gap: 1,
              justifyContent: "space-between",
              color: theme.colors.defaultBackground,
              width: "fit-content",
              minWidth: "100%",
              whiteSpace: "nowrap",
            }}
          >
            <Typography
              component="span"
              sx={{
                color: theme.colors.defaultBackground,
                fontWeight: 600,
                fontSize: "14px",
              }}
            >
              {successMessage}
            </Typography>
            <Check sx={{ fontSize: "14px" }} />
          </Box>
        )}
      </Box>
    </Box>
  );
};
